import { useEffect, useState } from 'react';
import { ImagePlus, Loader2, Sparkles, Star, X } from 'lucide-react';
import { GoogleBusinessIcon } from '../PlatformIcon';
import toast from 'react-hot-toast';
import { GBP_ENABLED } from '../../lib/featureFlags';
import {
  listGoogleReviewSocialUsage,
  type GoogleReviewChannelTag,
} from '../../lib/marketing/googleReviewSocialUsage';
import type { MediaItem } from '../../lib/mediaLibrary';
import { supabase } from '../../lib/supabaseClient';
import { invokeErrorMessage } from '../../lib/supabaseFunctionError';
import MediaLibraryPicker from '../MediaLibraryPicker';
import { IG_ASPECT_OPTIONS, type IgAspectKey } from './SocialImageCropEditor';

type StyleId = 'photo' | 'graphic' | 'product' | 'lifestyle' | 'before_after';

const STYLES: { id: StyleId; label: string; hint: string }[] = [
  { id: 'photo', label: 'Photo', hint: 'Realistic shot' },
  { id: 'graphic', label: 'Graphic', hint: 'Bold artwork' },
  { id: 'product', label: 'Showcase', hint: 'Finished work' },
  { id: 'lifestyle', label: 'Lifestyle', hint: 'Warm & inviting' },
  { id: 'before_after', label: 'Transform', hint: 'Before / after vibe' },
];

type ReviewRow = {
  id: string;
  rating?: number;
  starRating?: string;
  comment?: string | null;
  reviewer?: { displayName?: string; profilePhotoUrl?: string | null };
};

const STAR_MAP: Record<string, number> = {
  ONE: 1,
  TWO: 2,
  THREE: 3,
  FOUR: 4,
  FIVE: 5,
};

function starsFor(r: ReviewRow): number {
  if (typeof r.rating === 'number') return r.rating;
  if (r.starRating && STAR_MAP[r.starRating]) return STAR_MAP[r.starRating];
  return 0;
}

function igAspectToSize(aspectKey: IgAspectKey): '1024x1024' | '1024x1536' | '1536x1024' {
  if (aspectKey === '4:5') return '1024x1536';
  if (aspectKey === '1.91:1') return '1536x1024';
  return '1024x1024';
}

/**
 * Re-encode any media URL to a clean JPEG base64 for OpenAI edits.
 * Avoids "Invalid image file or mode" from odd MIME/containers (webp/heic/octet-stream).
 */
async function mediaUrlToJpegBase64(url: string, maxEdge = 2048): Promise<string> {
  const res = await fetch(url);
  if (!res.ok) throw new Error('Could not load base image');
  const blob = await res.blob();
  const bitmap = await createImageBitmap(blob);
  try {
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height, 1));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas not available');
    ctx.drawImage(bitmap, 0, 0, w, h);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    const base64 = dataUrl.split(',')[1];
    if (!base64 || base64.length < 100) throw new Error('Failed to encode base image');
    return base64;
  } finally {
    bitmap.close();
  }
}

export interface InstagramAiCreatePanelProps {
  businessId: string;
  businessName: string;
  aspectKey: IgAspectKey;
  onAspectChange: (next: IgAspectKey) => void;
  onClose: () => void;
  onCreated: (
    media: MediaItem,
    opts?: { suggestedCaption?: string; googleReviewId?: string },
  ) => void;
}

export default function InstagramAiCreatePanel({
  businessId,
  businessName,
  aspectKey,
  onAspectChange,
  onClose,
  onCreated,
}: InstagramAiCreatePanelProps) {
  const [mode, setMode] = useState<'generate' | 'review'>('generate');
  const [style, setStyle] = useState<StyleId>('photo');
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);

  const [gbpConnected, setGbpConnected] = useState(false);
  const [loadingGbp, setLoadingGbp] = useState(true);
  const [reviews, setReviews] = useState<ReviewRow[]>([]);
  const [reviewUsage, setReviewUsage] = useState<Map<string, GoogleReviewChannelTag[]>>(
    new Map(),
  );
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
  const [renderingReview, setRenderingReview] = useState(false);
  const [baseImage, setBaseImage] = useState<MediaItem | null>(null);
  const [showBasePicker, setShowBasePicker] = useState(false);

  useEffect(() => {
    if (!GBP_ENABLED || !businessId) {
      setLoadingGbp(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingGbp(true);
      try {
        const { data } = await supabase
          .from('google_business_connections')
          .select('id')
          .eq('business_id', businessId)
          .in('provider', ['ayrshare', 'google'])
          .eq('is_active', true)
          .limit(1)
          .maybeSingle();
        if (!cancelled) setGbpConnected(!!data?.id);
      } catch {
        if (!cancelled) setGbpConnected(false);
      } finally {
        if (!cancelled) setLoadingGbp(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  useEffect(() => {
    if (mode !== 'review' || !gbpConnected || !businessId) return;
    let cancelled = false;
    (async () => {
      setLoadingReviews(true);
      try {
        const { data, error } = await supabase.functions.invoke('ayrshare-gbp-reviews', {
          body: { businessId },
        });
        if (error || data?.error) {
          throw new Error(
            data?.error || (await invokeErrorMessage(error, data)) || 'Failed to load reviews',
          );
        }
        const list = (Array.isArray(data?.reviews) ? data.reviews : []) as ReviewRow[];
        const ranked = [...list]
          .filter((r) => starsFor(r) >= 4)
          .sort((a, b) => {
            const starDiff = starsFor(b) - starsFor(a);
            if (starDiff !== 0) return starDiff;
            return String(b.comment || '').length - String(a.comment || '').length;
          });
        const usage = await listGoogleReviewSocialUsage(
          businessId,
          ranked.map((r) => r.id).filter(Boolean),
        );
        if (!cancelled) {
          setReviews(ranked);
          setReviewUsage(usage);
          setSelectedReviewId(ranked[0]?.id || null);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setReviews([]);
          setReviewUsage(new Map());
          toast.error(err instanceof Error ? err.message : 'Failed to load Google reviews');
        }
      } finally {
        if (!cancelled) setLoadingReviews(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, gbpConnected, businessId]);

  const handleGenerate = async () => {
    if (prompt.trim().length < 8) {
      toast.error('Describe the image you want');
      return;
    }
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('ai-generate-social-image', {
        body: {
          businessId,
          style,
          prompt: prompt.trim(),
          size: igAspectToSize(aspectKey),
        },
      });
      if (error || data?.error) {
        const msg =
          data?.error ||
          (await invokeErrorMessage(error, data)) ||
          'Failed to generate image';
        if (data?.code === 'insufficient_tokens' || /token/i.test(String(msg))) {
          toast.error('Not enough AI tokens — purchase more in AI Settings');
        } else {
          toast.error(msg);
        }
        return;
      }
      const media = data?.media as MediaItem | undefined;
      if (!media?.id || !media?.url) {
        toast.error('AI did not return an image');
        return;
      }
      toast.success('Image created');
      onCreated(media);
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Failed to generate image');
    } finally {
      setGenerating(false);
    }
  };

  const handleRenderReview = async () => {
    const review = reviews.find((r) => r.id === selectedReviewId);
    if (!review) {
      toast.error('Pick a Google review');
      return;
    }
    setRenderingReview(true);
    try {
      const quote = String(review.comment || '').trim();
      const stars = starsFor(review);
      const reviewerName = review.reviewer?.displayName || 'Google user';

      let imageBase64: string | null = null;
      if (baseImage?.url) {
        try {
          imageBase64 = await mediaUrlToJpegBase64(baseImage.url);
        } catch (encodeErr) {
          console.error(encodeErr);
          toast.error(
            encodeErr instanceof Error
              ? encodeErr.message
              : 'Could not prepare the base image for AI',
          );
          return;
        }
      }

      const { data, error } = await supabase.functions.invoke('ai-generate-social-image', {
        body: {
          mode: 'google_review',
          businessId,
          businessName,
          reviewerName,
          rating: stars,
          quote,
          googleReviewId: review.id,
          size: igAspectToSize(aspectKey),
          baseImageUrl: baseImage?.url || null,
          imageBase64,
        },
      });
      if (error || data?.error) {
        const msg =
          data?.error ||
          (await invokeErrorMessage(error, data)) ||
          'Failed to generate review card';
        if (data?.code === 'insufficient_tokens' || /token/i.test(String(msg))) {
          toast.error('Not enough AI tokens — purchase more in AI Settings');
        } else {
          toast.error(msg);
        }
        return;
      }
      const media = data?.media as MediaItem | undefined;
      if (!media?.id || !media?.url) {
        toast.error('AI did not return a review card');
        return;
      }

      let suggestedCaption = quote
        ? `Grateful for reviews like this ⭐️\n\n“${quote.slice(0, 280)}${quote.length > 280 ? '…' : ''}”\n\nThank you, ${reviewerName}!`
        : `Grateful for ${stars}-star reviews like this ⭐️\n\nThank you, ${reviewerName}!`;

      // AI caption always loads Brand Guidelines server-side when available.
      try {
        const { data: captionData, error: captionError } = await supabase.functions.invoke(
          'social-auto-post-action',
          {
            body: {
              action: 'compose_caption',
              businessId,
              platform: 'instagram',
              imageUrls: [media.url],
              contextNote: [
                'This Instagram post shares a real Google review as a graphic.',
                `Reviewer: ${reviewerName}`,
                `Rating: ${stars}/5 stars`,
                quote
                  ? `Exact review quote (do not invent or alter meaning): "${quote}"`
                  : 'No written comment — celebrate the star rating.',
                'Write an Instagram-optimized grateful caption (hook + short body + 5–12 relevant hashtags).',
                'Never include URLs, markdown links, or domains. Soft CTA only (DM / link in bio).',
              ].join('\n'),
            },
          },
        );
        if (!captionError && !captionData?.error && String(captionData?.content || '').trim()) {
          suggestedCaption = String(captionData.content).trim();
        }
      } catch (captionErr) {
        console.warn('Brand-guided caption failed; using fallback', captionErr);
      }

      toast.success('Review card ready');
      onCreated(media, { suggestedCaption, googleReviewId: review.id });
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Failed to create review card');
    } finally {
      setRenderingReview(false);
    }
  };

  const busy = generating || renderingReview;

  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-3 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-blue-900 inline-flex items-center gap-1.5">
            <Sparkles className="h-4 w-4" />
            Create AI image
          </p>
          <p className="text-xs text-blue-800/80 mt-0.5">
            Generate a new social image, or AI-design a Google review card from Brand Guidelines.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="h-8 w-8 inline-flex items-center justify-center rounded-full text-blue-700 hover:bg-blue-100 disabled:opacity-50"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => setMode('generate')}
          disabled={busy}
          className={`rounded-lg border px-3 py-2 text-left text-sm font-semibold transition-colors disabled:opacity-50 ${
            mode === 'generate'
              ? 'border-blue-600 bg-white text-blue-900'
              : 'border-blue-100 bg-white/70 text-gray-700'
          }`}
        >
          <span className="inline-flex items-center gap-1.5">
            <ImagePlus className="h-3.5 w-3.5" />
            AI generate
          </span>
        </button>
        <button
          type="button"
          onClick={() => {
            if (!gbpConnected) {
              toast.error('Connect Google Business in Media Manager first');
              return;
            }
            setMode('review');
          }}
          disabled={busy || loadingGbp || !gbpConnected}
          className={`rounded-lg border px-3 py-2 text-left text-sm font-semibold transition-colors disabled:opacity-50 ${
            mode === 'review'
              ? 'border-blue-600 bg-white text-blue-900'
              : 'border-blue-100 bg-white/70 text-gray-700'
          }`}
          title={
            !gbpConnected
              ? 'Connect Google Business to use this preset'
              : 'Create a shareable Google review card'
          }
        >
          <span className="inline-flex items-center gap-1.5">
            <GoogleBusinessIcon className="h-3.5 w-3.5" />
            Google review
          </span>
          {!loadingGbp && !gbpConnected ? (
            <span className="block text-[10px] font-normal text-gray-500 mt-0.5">
              Connect GBP to unlock
            </span>
          ) : (
            <span className="block text-[10px] font-normal text-gray-500 mt-0.5">
              Real review → AI card
            </span>
          )}
        </button>
      </div>

      <div>
        <p className="text-xs font-medium text-gray-600 mb-1.5">Aspect ratio</p>
        <div className="flex gap-2">
          {IG_ASPECT_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => onAspectChange(opt.id)}
              disabled={busy}
              className={`flex-1 rounded-lg border px-2 py-2 text-xs font-semibold transition-colors disabled:opacity-50 ${
                aspectKey === opt.id
                  ? 'border-blue-600 bg-white text-blue-900'
                  : 'border-blue-100 bg-white/70 text-gray-700 hover:bg-white'
              }`}
            >
              {opt.label}
              <span className="block font-normal text-[10px] text-gray-500 mt-0.5">
                {opt.id}
              </span>
            </button>
          ))}
        </div>
        <p className="text-[11px] text-blue-800/70 mt-1.5">
          Image is generated at this ratio — pick it before creating.
        </p>
      </div>

      {mode === 'generate' ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {STYLES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setStyle(s.id)}
                disabled={busy}
                className={`rounded-lg border px-2.5 py-2 text-left transition-colors disabled:opacity-50 ${
                  style === s.id
                    ? 'border-blue-600 bg-white text-blue-900'
                    : 'border-blue-100 bg-white/70 text-gray-700 hover:bg-white'
                }`}
              >
                <span className="block text-xs font-semibold">{s.label}</span>
                <span className="block text-[10px] text-gray-500 mt-0.5">{s.hint}</span>
              </button>
            ))}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              Describe the image
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={3}
              disabled={busy}
              placeholder="e.g. A freshly detailed black SUV gleaming under soft garage lights, cinematic, no people…"
              className="w-full text-sm rounded-lg border border-blue-200 bg-white px-3 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-50"
            />
          </div>
          <button
            type="button"
            onClick={() => void handleGenerate()}
            disabled={busy || prompt.trim().length < 8}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {generating ? 'Generating…' : 'Generate image'}
          </button>
        </>
      ) : (
        <>
          <div className="rounded-lg border border-blue-100 bg-white/80 p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-xs font-semibold text-gray-800">Base image (optional)</p>
                <p className="text-[11px] text-gray-500">
                  AI uses this photo as the scene behind the review graphic
                </p>
              </div>
              {baseImage ? (
                <button
                  type="button"
                  onClick={() => setBaseImage(null)}
                  disabled={busy}
                  className="text-xs font-semibold text-red-600 hover:text-red-700 disabled:opacity-50"
                >
                  Remove
                </button>
              ) : null}
            </div>
            {baseImage ? (
              <div className="relative overflow-hidden rounded-lg border border-gray-200">
                <img
                  src={baseImage.url}
                  alt={baseImage.file_name || 'Base'}
                  className="h-28 w-full object-cover"
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowBasePicker(true)}
                disabled={busy}
                className="w-full flex items-center justify-center gap-2 rounded-lg border border-dashed border-blue-200 px-3 py-4 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50"
              >
                <ImagePlus className="h-3.5 w-3.5" />
                Select from Media library
              </button>
            )}
          </div>

          {loadingReviews ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading Google reviews…
            </div>
          ) : reviews.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              No 4–5 star Google reviews found yet.
            </p>
          ) : (
            <div className="space-y-2 max-h-56 overflow-y-auto pr-0.5">
              {reviews.map((r) => {
                const stars = starsFor(r);
                const selected = r.id === selectedReviewId;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setSelectedReviewId(r.id)}
                    disabled={busy}
                    className={`w-full rounded-lg border px-3 py-2.5 text-left transition-colors disabled:opacity-50 ${
                      selected
                        ? 'border-blue-600 bg-white'
                        : 'border-blue-100 bg-white/70 hover:bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-1 mb-1">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          className={`h-3.5 w-3.5 ${
                            i < stars ? 'fill-amber-400 text-amber-400' : 'text-gray-300'
                          }`}
                        />
                      ))}
                      <span className="ml-1 text-xs font-medium text-gray-700 truncate">
                        {r.reviewer?.displayName || 'Google user'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 line-clamp-2">
                      {r.comment?.trim() || '(No written comment)'}
                    </p>
                    {(reviewUsage.get(r.id) || []).length > 0 ? (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {(reviewUsage.get(r.id) || []).map((tag) => (
                          <span
                            key={`${tag.connectionId}-${tag.platform}`}
                            className="inline-flex items-center rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600"
                          >
                            Posted · {tag.platform === 'facebook' ? 'Facebook' : tag.platform === 'linkedin' ? 'LinkedIn' : 'Instagram'}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </button>
                );
              })}
            </div>
          )}
          <button
            type="button"
            onClick={() => void handleRenderReview()}
            disabled={busy || !selectedReviewId || reviews.length === 0}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {renderingReview ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            {renderingReview ? 'Creating with AI…' : 'Generate review card'}
          </button>
          <p className="text-[11px] text-blue-800/80 text-center">
            Design + caption use your Brand Guidelines. Exact review text is locked in the prompt.
          </p>
        </>
      )}

      <MediaLibraryPicker
        isOpen={showBasePicker}
        businessId={businessId}
        multiple={false}
        zIndex={100230}
        initialSelectedUrls={baseImage ? [baseImage.url] : []}
        onClose={() => setShowBasePicker(false)}
        onDone={(selected) => {
          setBaseImage(selected[0] || null);
          setShowBasePicker(false);
        }}
      />
    </div>
  );
}
